'use client';

import './sac-card.scss';

import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { SkeletonImage } from '@sola/ark-core/atoms/skeleton-image/skeleton-image.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import {
  ChildImageVariantEnum,
  getChildImageUrl,
} from '@sola/ark-core/utils/child-image.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';

import { Image } from '../../atoms/image/image.tsx';
import { useSacImageAltText } from '../../utils/alt-text.ts';

export type SacCardProps = ChildAvailabilityItem & {
  badgeText?: string;
  href?: string;
};

export const SacCard: React.FC<SacCardProps> = ({
  name: { first: firstName, preferred: preferredName },
  imageUrl,
  birthDate,
  icp: { country },
  minDaysWaiting,
  age,
  globalId,
  sourceCode,
  gender,
  isSpecialNeeds,
  isOrphan,
  isInHivAffectedArea,
  badgeText,
  href,
}) => {
  const { t } = useTranslation(['children', 'geo']);
  const childName = preferredName || firstName;

  const childImage = getChildImageUrl(
    imageUrl,
    ChildImageVariantEnum.Full,
    500,
  );

  const titleAndAlt = useSacImageAltText(
    gender,
    country,
    undefined,
    minDaysWaiting,
    childName,
    isOrphan,
    isSpecialNeeds,
    isInHivAffectedArea,
  );

  const content = (
    <>
      <div className="relative">
        <div className="SacCard__badge absolute m-comfy-sm">
          <Badge
            color="brand"
            label={
              badgeText ??
              t('children:child.waiting', { numDays: minDaysWaiting })
            }
          />
        </div>
        <Image
          image={
            childImage
              ? {
                  url: childImage,
                  alt: titleAndAlt,
                }
              : undefined
          }
          loader={loader}
          sizeIntent="third"
          rounded="lg"
          aspectRatio="3-4"
          objectPosition="top"
          hasPlaceholder
        />
      </div>
      <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">
        <span className="SacCard__name heading heading-sm">{childName}</span>
        <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs body body-md">
          <span className=" SacCard__glyph-text flex gap-comfy-xs items-center">
            <ArkIcon iconName="birthday-cake" />
            {t('children:sac.age', {
              count: Number(age),
              birthday: birthDate,
            })}
          </span>
          {country && (
            <span className="SacCard__glyph-text flex gap-comfy-xs items-center">
              <ArkIcon iconName="globe-05" />
              {country}
            </span>
          )}
        </div>
      </div>
    </>
  );

  const childBioHref =
    href ??
    `/sponsor-a-child/child-bio?gid=${globalId}${sourceCode ? '&source=' + sourceCode : ''}`;

  return (
    <div className="flex flex-col relative">
      <Link
        href={childBioHref}
        className="SacCard__bio-link"
        title={titleAndAlt}
      >
        {content}
      </Link>
    </div>
  );
};

export const SacCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col">
      <div className="relative">
        <div className="SacCard__badge absolute m-comfy-sm">
          <Skeleton height={24} width={100} borderRadius="9999px" />
        </div>

        <SkeletonImage
          width="100%"
          height="auto"
          style={{ aspectRatio: '3/4' }}
          borderRadius="1em"
        />
      </div>

      <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">
        <span className="SacCard__name">
          <Skeleton height={24} width="70%" />
        </span>

        <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs">
          <div className="SacCard__glyph-text flex gap-comfy-xs items-center">
            <Skeleton height={20} width={20} borderRadius="0.25rem" />
            <Skeleton height={20} width={180} />
          </div>
          <div className="SacCard__glyph-text flex gap-comfy-xs items-center">
            <Skeleton height={20} width={20} borderRadius="0.25rem" />
            <Skeleton height={20} width={150} />
          </div>
        </div>
      </div>
    </div>
  );
};
